import { useTranslation } from 'react-i18next';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, styled } from '@mui/material';
import Label from '@/components/@IntranetLibrary/Label';
import Select from '@/components/@IntranetLibrary/Select';
import { Caption } from '@/components/@IntranetLibrary/Typography';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildCorporateSealSubject,
  changeSealCompany,
  LAW_CONFIRM_OPTIONS,
  SEAL_COMPANY_OPTIONS,
  sealOptionsOf,
  type CorporateSealFormState,
} from '@/utils/approval/corporateSeal';
import { FieldBlock } from './styles';
import type { ApprovalFormValues } from './types';

const SelectRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 20,
});

const SelectItem = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
});

const selectSx = { ...approvalOutlineSelectSx, minWidth: 200 };

/** 법인인감 날인 신청(113) — 법인·인감 종류·법무팀 검토 여부 */
const CorporateSealField = () => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<ApprovalFormValues>();
  const state = useWatch({ control, name: 'corporateSeal' });

  if (!state) return null;

  const update = (next: CorporateSealFormState) => {
    setValue('corporateSeal', next);
    setValue('subject', buildCorporateSealSubject(next));
  };

  return (
    <FieldBlock>
      <SelectRow>
        <SelectItem>
          <Label size={14} isRequired={false} title={t('법인 구분')} />
          <Select
            value={state.company}
            options={SEAL_COMPANY_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.label),
            }))}
            onChange={(e) =>
              update(changeSealCompany(state, e.target.value as string))
            }
            sx={selectSx}
          />
        </SelectItem>
        <SelectItem>
          <Label size={14} isRequired={false} title={t('인감 종류')} />
          <Select
            value={state.seal}
            options={sealOptionsOf(state.company).map((o) => ({
              value: o.value,
              label: t(o.label),
            }))}
            onChange={(e) =>
              update({ ...state, seal: e.target.value as string })
            }
            sx={selectSx}
          />
        </SelectItem>
        <SelectItem>
          <Label size={14} isRequired={false} title={t('법무팀 검토 여부')} />
          <Select
            value={state.lawConfirm}
            options={LAW_CONFIRM_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.label),
            }))}
            onChange={(e) =>
              update({ ...state, lawConfirm: e.target.value as string })
            }
            sx={selectSx}
          />
          <Caption sx={{ color: 'var(--dk-danger, #f55060)' }}>
            {t('※ E-law 계약서 화면 첨부 필수')}
          </Caption>
        </SelectItem>
      </SelectRow>
    </FieldBlock>
  );
};

export default CorporateSealField;
